import {
    FormControl,
    FormLabel,
    Input,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Select,
    Textarea,
    VStack,
    useToast,
    Text,
    HStack,
    IconButton,
    Badge,
    Flex,
    RadioGroup,
    Radio,
    Stack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdAdd, MdDelete } from 'react-icons/md';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import {
    CampaignIncentiveType,
    CampaignRuleType,
    CampaignScheduleFrequency,
} from '../../../models/campaign/dto/campaign.dto';
import { CreateCampaignInputDto } from '../../../models/campaign/dto/campaign-input.dto';
import { useListCoupons } from '../../coupon-management/hooks/useListCoupons';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useCreateCampaign } from '../hooks/useCreateCampaign';
import { isUnreadable } from '../../analytics/utils/statValue';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export default function CreateCampaignModal({ isOpen, onClose }: Props) {
    const toast = useToast();
    const { mutateAsync: createCampaign, isLoading } = useCreateCampaign();
    const { selectedOrganization } = useSelectedOrganization();
    // The coupon select below is `isRequired`, so a refused read used to block
    // campaign creation with an empty dropdown and no explanation.
    // See `coupon-read-error-gate.test.ts`.
    const { data: coupons, isError: isCouponsError } = useListCoupons(selectedOrganization?.id || '');

    const [name, setName] = useState('');
    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationBody, setNotificationBody] = useState('');
    const [frequency, setFrequency] = useState<CampaignScheduleFrequency>(CampaignScheduleFrequency.DAILY);
    const [dayOfWeek, setDayOfWeek] = useState(1);
    const [hour, setHour] = useState(10);
    const [timezone, setTimezone] = useState('Europe/Paris');
    const [rules, setRules] = useState<{ type: CampaignRuleType; value: number }[]>([
        { type: CampaignRuleType.INACTIVE_DAYS, value: 14 },
    ]);
    const [incentiveType, setIncentiveType] = useState<CampaignIncentiveType>(CampaignIncentiveType.NONE);
    const [couponId, setCouponId] = useState('');
    const [creditAmount, setCreditAmount] = useState(0);
    const [conversionWindowHours, setConversionWindowHours] = useState(72);
    const [runAt, setRunAt] = useState('');

    const resetForm = () => {
        setName('');
        setNotificationTitle('');
        setNotificationBody('');
        setFrequency(CampaignScheduleFrequency.DAILY);
        setDayOfWeek(1);
        setHour(10);
        setTimezone('Europe/Paris');
        setRules([{ type: CampaignRuleType.INACTIVE_DAYS, value: 14 }]);
        setIncentiveType(CampaignIncentiveType.NONE);
        setCouponId('');
        setCreditAmount(0);
        setConversionWindowHours(72);
        setRunAt('');
    };

    const handleSubmit = async () => {
        if (!name || !notificationTitle || !notificationBody || rules.length === 0) {
            toast({
                title: translate('campaign.validation_error'),
                status: 'warning',
                duration: 3000,
            });
            return;
        }

        if (frequency === CampaignScheduleFrequency.ONE_TIME && !runAt) {
            toast({
                title: translate('campaign.validation_error'),
                status: 'warning',
                duration: 3000,
            });
            return;
        }

        const input: CreateCampaignInputDto = {
            name,
            notificationTitle,
            notificationBody,
            schedule: {
                frequency,
                ...(frequency === CampaignScheduleFrequency.WEEKLY ? { dayOfWeek } : {}),
                ...(frequency === CampaignScheduleFrequency.ONE_TIME ? { runAt: new Date(runAt).toISOString() } : {}),
                hour,
                timezone,
            },
            rules,
            incentiveType,
            ...(incentiveType === CampaignIncentiveType.COUPON ? { couponId } : {}),
            ...(incentiveType === CampaignIncentiveType.CREDITS ? { creditAmount } : {}),
            conversionWindowHours,
        };

        try {
            await createCampaign(input);
            toast({
                title: translate('campaign.created_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            resetForm();
            onClose();
        } catch (error: any) {
            toast({
                title: translate('campaign.create_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const addRule = () => {
        setRules([...rules, { type: CampaignRuleType.INACTIVE_DAYS, value: 7 }]);
    };

    const removeRule = (index: number) => {
        setRules(rules.filter((_, i) => i !== index));
    };

    const updateRule = (index: number, field: 'type' | 'value', val: string | number) => {
        const updated = [...rules];
        updated[index] = { ...updated[index], [field]: field === 'value' ? Number(val) : val };
        setRules(updated);
    };

    const DAY_LABELS = [
        translate('campaign.monday'),
        translate('campaign.tuesday'),
        translate('campaign.wednesday'),
        translate('campaign.thursday'),
        translate('campaign.friday'),
        translate('campaign.saturday'),
        translate('campaign.sunday'),
    ];

    return (
        <FormModal
            isOpen={isOpen}
            onClose={onClose}
            title={translate('campaign.create_campaign')}
            onSubmit={handleSubmit}
            submitLabel={translate('campaign.create')}
            cancelLabel={translate('common.cancel')}
            isSubmitting={isLoading}
            size="2xl"
        >
                    <FormStack density="section">
                        <FormControl isRequired>
                            <FormLabel>{translate('campaign.name')}</FormLabel>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={translate('campaign.name_placeholder')}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>{translate('campaign.notification_title')}</FormLabel>
                            <Input
                                value={notificationTitle}
                                onChange={(e) => setNotificationTitle(e.target.value)}
                                placeholder={translate('campaign.notification_title_placeholder')}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>{translate('campaign.notification_body')}</FormLabel>
                            <Textarea
                                value={notificationBody}
                                onChange={(e) => setNotificationBody(e.target.value)}
                                placeholder={translate('campaign.notification_body_placeholder')}
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                {translate('campaign.template_hint')}
                            </Text>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>{translate('campaign.frequency')}</FormLabel>
                            <Select value={frequency} onChange={(e) => setFrequency(e.target.value as CampaignScheduleFrequency)}>
                                <option value={CampaignScheduleFrequency.DAILY}>{translate('campaign.daily')}</option>
                                <option value={CampaignScheduleFrequency.WEEKLY}>{translate('campaign.weekly')}</option>
                                <option value={CampaignScheduleFrequency.ONE_TIME}>{translate('campaign.one_time')}</option>
                            </Select>
                        </FormControl>

                        {frequency === CampaignScheduleFrequency.WEEKLY && (
                            <FormControl>
                                <FormLabel>{translate('campaign.day_of_week')}</FormLabel>
                                <Select value={dayOfWeek} onChange={(e) => setDayOfWeek(Number(e.target.value))}>
                                    {DAY_LABELS.map((label, i) => (
                                        <option key={i + 1} value={i + 1}>{label}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {frequency === CampaignScheduleFrequency.ONE_TIME && (
                            <FormControl isRequired>
                                <FormLabel>{translate('campaign.run_at')}</FormLabel>
                                <Input
                                    type="datetime-local"
                                    value={runAt}
                                    onChange={(e) => setRunAt(e.target.value)}
                                />
                            </FormControl>
                        )}

                        {frequency !== CampaignScheduleFrequency.ONE_TIME && (
                            <HStack w="100%" spacing={4}>
                                <FormControl isRequired>
                                    <FormLabel>{translate('campaign.hour')}</FormLabel>
                                    <NumberInput min={0} max={23} value={hour} onChange={(_, val) => setHour(val)}>
                                        <NumberInputField />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>{translate('campaign.timezone')}</FormLabel>
                                    <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                                        <option value="Europe/Paris">Europe/Paris</option>
                                        <option value="Europe/London">Europe/London</option>
                                        <option value="UTC">UTC</option>
                                        <option value="America/New_York">America/New_York</option>
                                    </Select>
                                </FormControl>
                            </HStack>
                        )}

                        <FormControl isRequired>
                            <Flex justify="space-between" align="center" mb={2}>
                                <FormLabel mb={0}>{translate('campaign.rules')}</FormLabel>
                                <IconButton
                                    aria-label={translate('campaign.add_rule')}
                                    icon={<MdAdd />}
                                    size="xs"
                                    colorScheme="brand"
                                    onClick={addRule}
                                />
                            </Flex>
                            <VStack spacing={2}>
                                {rules.map((rule, index) => (
                                    <HStack key={index} w="100%">
                                        <Select
                                            size="sm"
                                            value={rule.type}
                                            onChange={(e) => updateRule(index, 'type', e.target.value)}
                                        >
                                            <option value={CampaignRuleType.INACTIVE_DAYS}>{translate('campaign.rule_inactive_days')}</option>
                                            <option value={CampaignRuleType.NO_GAME_DAYS}>{translate('campaign.rule_no_game_days')}</option>
                                            <option value={CampaignRuleType.NO_EVENT_DAYS}>{translate('campaign.rule_no_event_days')}</option>
                                        </Select>
                                        <NumberInput
                                            size="sm"
                                            min={1}
                                            max={365}
                                            value={rule.value}
                                            onChange={(_, val) => updateRule(index, 'value', val)}
                                            w="120px"
                                        >
                                            <NumberInputField />
                                        </NumberInput>
                                        <Badge fontSize="xs">{translate('campaign.days')}</Badge>
                                        {rules.length > 1 && (
                                            <IconButton
                                                aria-label={translate('campaign.remove_rule')}
                                                icon={<MdDelete />}
                                                size="xs"
                                                variant="ghost"
                                                colorScheme="red"
                                                onClick={() => removeRule(index)}
                                            />
                                        )}
                                    </HStack>
                                ))}
                            </VStack>
                        </FormControl>

                        <FormControl>
                            <FormLabel>{translate('campaign.incentive')}</FormLabel>
                            <RadioGroup value={incentiveType} onChange={(val) => setIncentiveType(val as CampaignIncentiveType)}>
                                <Stack direction="row" spacing={4}>
                                    <Radio value={CampaignIncentiveType.NONE}>{translate('campaign.incentive_none')}</Radio>
                                    <Radio value={CampaignIncentiveType.COUPON}>{translate('campaign.incentive_coupon')}</Radio>
                                    <Radio value={CampaignIncentiveType.CREDITS}>{translate('campaign.incentive_credits')}</Radio>
                                </Stack>
                            </RadioGroup>
                        </FormControl>

                        {incentiveType === CampaignIncentiveType.COUPON && (
                            <FormControl isRequired>
                                <FormLabel>{translate('campaign.select_coupon')}</FormLabel>
                                {/* `isUnreadable`, not the raw flag: React Query KEEPS `coupons` through
                                    a failed background refetch, and this arm REPLACES the control. Reading
                                    the flag alone took a working required coupon picker
                                    away from the operator because a refresh was refused
                                    (useListCoupons, staleTime 0 — R132-1). */}
                                {isUnreadable(isCouponsError, coupons) ? (
                                    <InlineErrorState message={translate('common.unexpected_error')} />
                                ) : (
                                    <Select
                                        placeholder={translate('campaign.select_coupon')}
                                        value={couponId}
                                        onChange={(e) => setCouponId(e.target.value)}
                                    >
                                        {coupons?.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </Select>
                                )}
                            </FormControl>
                        )}

                        {incentiveType === CampaignIncentiveType.CREDITS && (
                            <FormControl isRequired>
                                <FormLabel>{translate('campaign.credit_amount')}</FormLabel>
                                <NumberInput min={0.01} value={creditAmount} onChange={(_, val) => setCreditAmount(val)}>
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>
                        )}

                        <FormControl>
                            <FormLabel>{translate('campaign.conversion_window')}</FormLabel>
                            <Select
                                value={conversionWindowHours}
                                onChange={(e) => setConversionWindowHours(Number(e.target.value))}
                            >
                                <option value={24}>{translate('campaign.conversion_window_24h')}</option>
                                <option value={48}>{translate('campaign.conversion_window_48h')}</option>
                                <option value={72}>{translate('campaign.conversion_window_72h')}</option>
                                <option value={168}>{translate('campaign.conversion_window_7d')}</option>
                            </Select>
                        </FormControl>
                    </FormStack>
        </FormModal>
    );
}
